import { NavLink, Outlet } from 'react-router';
import { useSession, useSignOut } from '../lib/session';
import { setViewAs } from '../lib/view-mode';
import { Banner, Button, Spinner } from './ui';
import { CapMark, Wordmark } from './Brand';
import { Login } from '../pages/Login';

function Tab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => `navpill ${isActive ? 'navpill-on' : 'navpill-off'}`}
    >
      {children}
    </NavLink>
  );
}

export function Shell() {
  const session = useSession();
  const signOut = useSignOut();

  if (session.isPending) return <Spinner label="Opening the shop" />;
  if (session.isError) {
    return (
      <div className="mx-auto max-w-lg px-6 py-20">
        <Banner tone="error">Cannot reach the shop. {session.error.message}</Banner>
      </div>
    );
  }
  // Nothing at all is visible without a login — including the catalogue.
  if (!session.data) return <Login />;

  const me = session.data;
  const staff = me.capabilities.manageAssignedStores;
  const previewing = me.viewingAs !== null;

  return (
    <div className={`min-h-dvh ${previewing ? 'pt-11' : ''}`}>
      {/*
        A preview that is easy to forget you are in is worse than no preview:
        an owner would conclude a feature is broken when it is simply hidden from
        the role they are wearing. So this bar is fixed, full width, and in a
        colour used nowhere else in the app.
      */}
      {previewing && (
        <div className="fixed inset-x-0 top-0 z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-ochre-500 px-4 py-2.5 text-center">
          <span className="sign text-[11px] text-ink-950">
            Viewing as {me.viewingAs} · you are really {me.actualRole}
          </span>
          <button
            type="button"
            onClick={() => setViewAs(null)}
            className="sign rounded-[3px] border border-ink-950/30 bg-paper-50/80 px-2.5 py-1 text-[10px] text-ink-950 hover:bg-paper-50"
          >
            Back to {me.actualRole}
          </button>
        </div>
      )}

      <div className="min-h-dvh">
        <header className="sticky top-0 z-30 border-b hairline bg-paper-50/85 backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-3 px-6 py-3">
            <NavLink to="/" className="flex items-center gap-2.5" aria-label="bigtoto.shop home">
              <CapMark className="h-7 w-7 text-ochre-500" />
              <Wordmark />
            </NavLink>

            <nav className="order-3 flex w-full flex-wrap gap-1 border-t hairline pt-2.5 md:order-none md:w-auto md:border-0 md:pt-0">
              <Tab to="/">Shop</Tab>
              <Tab to="/purchases">Purchases</Tab>
              {staff && <Tab to="/manage">Manage</Tab>}
              {me.capabilities.viewStatistics && <Tab to="/statistics">Statistics</Tab>}
              {me.capabilities.manageUsers && <Tab to="/people">People</Tab>}
              {me.capabilities.resetDatabase && <Tab to="/danger">Danger</Tab>}
            </nav>

            <div className="ml-auto flex items-center gap-3">
              <span className="text-right text-[13px] leading-tight text-ink-500">
                <span className="block text-ink-900">{me.name}</span>
                <span className="sign text-[10px]">
                  {previewing ? `${me.actualRole} as ${me.viewingAs}` : me.role}
                </span>
              </span>

              {me.canViewAs.length > 0 && (
                <label className="text-[13px]">
                  <span className="sr-only">View the app as another role</span>
                  <select
                    value={me.viewingAs ?? ''}
                    onChange={(event) => setViewAs((event.target.value || null) as never)}
                    className="sign rounded-[3px] border border-paper-300 bg-paper-50 px-2 py-1.5 text-[11px] text-ink-700"
                  >
                    <option value="">View as {me.actualRole}</option>
                    {me.canViewAs.map((role) => (
                      <option key={role} value={role}>
                        View as {role}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <Button variant="quiet" onClick={() => signOut.mutate()}>
                Sign out
              </Button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-6 py-8">
          <Outlet />
        </main>

        <footer className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-3 gap-y-1 px-6 pb-10 pt-4 text-[12px] text-ink-500">
          <CapMark className="h-3.5 w-3.5 text-paper-400" />
          <span>bigtoto.shop</span>
          <span aria-hidden>·</span>
          <span>Prices in AUD. Sold by the unit.</span>
          <span aria-hidden>·</span>
          <span>Day and week totals use Broken Hill time.</span>
        </footer>
      </div>
    </div>
  );
}
